// ============================================================================
// @xjlv2/ui Base UI & Design System Component Library
// Layer 1: Base Atomic Components + UI Utilities & Composables
// ============================================================================

// Base Atomic Components (Ant Design style)
export { default as RrButton } from './components/button/RrButton.vue'
export { default as RrInput } from './components/input/RrInput.vue'
export { default as RrInputNumber } from './components/input/RrInputNumber.vue'
export { default as RrSelect } from './components/select/RrSelect.vue'
import DatePickerComponent from './components/datepicker/RrDatePicker.vue'
import RangePickerComponent from './components/datepicker/RrDateRangePicker.vue'
export const RrDatePicker = Object.assign(DatePickerComponent, { RangePicker: RangePickerComponent })
export { default as RrTimePicker } from './components/datepicker/RrTimePicker.vue'
export { createDefaultDateRangePresets } from './components/datepicker/dateValue'
export type { PickerValue, PickerFormat } from './components/datepicker/dateValue'
export type { DatePickerProps, RangePickerProps, TimePickerProps } from './components/datepicker/types'
export { default as RrDateRangePicker } from './components/datepicker/RrDateRangePicker.vue'
export { default as RrRangePicker } from './components/datepicker/RrDateRangePicker.vue'
export { default as RrColorPicker } from './components/color-picker/RrColorPicker.vue'
import RrMessageComponent from './components/message/RrMessage.vue'
import {
  message,
  type MessageHandle,
  type MessageApi
} from './components/message/message'

export { message }
export type { MessageHandle, MessageApi }
export const RrMessage: typeof RrMessageComponent & MessageApi = Object.assign(
  RrMessageComponent,
  message
)
import UploadComponent from './components/upload/RrUpload.vue'
import { UPLOAD_LIST_IGNORE } from './components/upload/types'
export const RrUpload = Object.assign(UploadComponent, { LIST_IGNORE: UPLOAD_LIST_IGNORE })
export type { RcFile, UploadProps, UploadRequestOptions, UploadRequestHandle } from './components/upload/types'
export { default as RrSteps } from './components/steps/RrSteps.vue'
export { default as RrStep } from './components/steps/RrStep.vue'
export { default as RrTree } from './components/tree/RrTree.vue'
export { default as RrTreeNode } from './components/tree/RrTreeNode.vue'
export { default as RrForm } from './components/form/RrForm.vue'
export { default as RrFormItem } from './components/form/RrFormItem.vue'
export { default as RrDropdown } from './components/dropdown/RrDropdown.vue'
export { default as RrMenu } from './components/dropdown/RrMenu.vue'
export { default as RrMenuItem } from './components/dropdown/RrMenuItem.vue'
export { default as RrCheckbox } from './components/checkbox/RrCheckbox.vue'
export { default as RrCheckboxGroup } from './components/checkbox/RrCheckboxGroup.vue'
export { default as RrRadio } from './components/radio/RrRadio.vue'
export { default as RrRadioGroup } from './components/radio/RrRadioGroup.vue'
export { default as RrSwitch } from './components/switch/RrSwitch.vue'
export { default as RrTag } from './components/tag/RrTag.vue'
export { default as RrBadge } from './components/badge/RrBadge.vue'
export { default as RrEmpty } from './components/empty/RrEmpty.vue'
export { default as RrSkeleton } from './components/skeleton/RrSkeleton.vue'
export { default as RrModal } from './components/modal/RrModal.vue'
export { default as RrDrawer } from './components/drawer/RrDrawer.vue'
export { default as RrPagination } from './components/pagination/RrPagination.vue'
export { default as RrTable } from './components/table/RrTable.vue'
export { default as RrSpace } from './components/space/RrSpace.vue'
export { default as RrDivider } from './components/divider/RrDivider.vue'
export { default as RrCard } from './components/card/RrCard.vue'
export { default as RrStatistic } from './components/statistic/RrStatistic.vue'
export { default as RrPopconfirm } from './components/popconfirm/RrPopconfirm.vue'
export { default as RrTooltip } from './components/tooltip/RrTooltip.vue'
export { default as RrTabs } from './components/tabs/RrTabs.vue'

// Types
export type {
  AdminMetricItem,
  MetricItem,
  TableColumnType,
  TableColumnItem,
  TableRowSelection,
  TableFilters,
  TableSorter,
  TableSortOrder,
  TableSize,
  TableKey,
  TablePaginationConfig,
  TableChangeExtra,
  TableDensity,
  PageItem,
  SemanticTone,
  BadgeStatus,
  TagPresetStatus,
  TagColor,
  StatusMeta,
  ButtonType,
  ButtonSize,
  DrawerPlacement,
  SelectOption,
  SelectProps,
  DateRangePreset,
  UploadFile,
  TreeNodeData,
  MessageType,
  TabItem,
  CheckboxOption,
  RadioOption,
  FormContext,
  FormItemContext,
  FormInstance,
  FormItemInstance,
  Rule,
  RuleObject,
  RuleType,
  RuleTrigger,
  NamePath,
  ValidateErrorEntity,
  StepsContext
} from './types'

// Composables
export { usePagination } from './composables/usePagination'
export type { PaginationOptions, UsePagination } from './composables/usePagination'
export { useTableColumns } from './composables/useTableColumns'
export type { UseTableColumns } from './composables/useTableColumns'
export { useTableUrlSync } from './composables/useTableUrlSync'
export type { UseTableUrlSyncOptions } from './composables/useTableUrlSync'
export { useListSort } from './composables/useListSort'
export type { UseListSort } from './composables/useListSort'
export { useDragReorder } from './composables/useDragReorder'
export type { DragReorderHandle, DragReorderOptions } from './composables/useDragReorder'
export { useScrollLock, lockScroll, unlockScroll } from './composables/useScrollLock'

// Utilities
export { calculatePageCount, clampPage, generatePageItems } from './utils/pagination'
export {
  isColumnVisible,
  toggleColumnVisibility,
  setColumnVisibility,
  resetColumnsToDefault
} from './utils/table'
export { applySort, nextSortState, sortParams } from './utils/sort'
export type { SortDirection, SortState } from './utils/sort'
export { moveItem } from './utils/dragOrder'
export {
  parseUrlQuery,
  serializeUrlQuery,
  updateBrowserUrl
} from './utils/urlSync'
export type { QueryParamPrimitive } from './utils/urlSync'

// Metric utilities (backward compatibility)
export function formatCount(value: number): string {
  return new Intl.NumberFormat('zh-CN').format(value)
}

// Status Dictionaries (Ant Design Pro valueEnum style)
export { CommonStatusMap } from './constants/status'

export { default as RrConfigProvider } from './components/config-provider/RrConfigProvider.vue'
export type { ConfigProviderProps, UiLocale } from './components/config-provider/context'
export { default as RrAlert } from './components/alert/RrAlert.vue'
export { default as RrSpin } from './components/spin/RrSpin.vue'
import DescriptionsComponent from './components/descriptions/RrDescriptions.vue'
import DescriptionsItem from './components/descriptions/RrDescriptionsItem.vue'
export const RrDescriptions = Object.assign(DescriptionsComponent, { Item: DescriptionsItem })
export { DescriptionsItem as RrDescriptionsItem }
import ImageComponent from './components/image/RrImage.vue'
import ImagePreviewGroup from './components/image/RrImagePreviewGroup.vue'
export const RrImage = Object.assign(ImageComponent, { PreviewGroup: ImagePreviewGroup })
export { ImagePreviewGroup as RrImagePreviewGroup }
export type { ImageProps, ImagePreviewOptions } from './components/image/types'

export type { ModalProps } from './components/modal/types'
export type { DrawerProps } from './components/drawer/types'
export type { PaginationProps } from './components/pagination/types'
export { useBoundValue } from './composables/useBoundValue'
